import type { ConfigDTO, MeDTO, UpdateMeInput } from '@giftgroup/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, post, put } from './api';
import { queryKeys } from './queryClient';

/** Who I am, which families I'm in, and what needs my attention (doc 04). */
export function useMe(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => get<MeDTO>('/me', signal),
    enabled: options.enabled ?? true,
    retry: false,
  });
}

/** Feature flags. The client learns these only from the server (doc 02). */
export function useConfig() {
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: ({ signal }) => get<ConfigDTO>('/config', signal),
    staleTime: 10 * 60_000,
  });
}

/** Timezone and the master "pause all emails" switch (doc 11 › Preferences). */
export function useUpdateMe() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMeInput) => patch<MeDTO>('/me', input),
    onSuccess: (me) => client.setQueryData(queryKeys.me, me),
  });
}

export function useSwitchFamily() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (familyId: string) => put<void>('/me/active-family', { familyId }),
    onSuccess: () => client.invalidateQueries(),
  });
}

export function useDismissHint() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => post<void>('/me/hints/dismiss', { key }),
    // Optimistic: dismissing a hint should feel instant, and it's harmless if it fails.
    onMutate: async (key) => {
      await client.cancelQueries({ queryKey: queryKeys.me });
      const previous = client.getQueryData<MeDTO>(queryKeys.me);
      if (previous)
        client.setQueryData<MeDTO>(queryKeys.me, {
          ...previous,
          dismissedHints: [...previous.dismissedHints, key],
        });
      return { previous };
    },
    onError: (_err, _key, context) => {
      if (context?.previous) client.setQueryData(queryKeys.me, context.previous);
    },
  });
}

/** The family the app is currently showing, resolved from `/me`. */
export function useActiveFamily(me: MeDTO | undefined) {
  const activeId = me?.activeFamilyId ?? null;
  return me?.families.find((f) => f.id === activeId) ?? me?.families[0] ?? null;
}
